import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-primary'}`} data-id="element-16" data-mp="1ir42qbsw3r2t">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-17" data-mp="2z2htxjt3yv69">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-18" data-mp="1bom219iayy4r">
      <Eyebrow data-id="element-19" data-mp="16qkhek58aqr6">⭐ 4,9/5 · +200 instructores</Eyebrow>
      <SectionTitle data-id="element-20" data-mp="16r3rjeon0aun">
        Lo que dicen los que <span className="text-primary" data-id="element-21" data-mp="12oyxwfy6ve80">ya lo están usando</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-22" data-mp="12qtmc48o5ogu">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-pink-100 bg-white p-6 shadow-sm" data-id="element-23" data-mp="xs06w69tnne1">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/15" data-id="element-24" data-mp="nzay5j5nonme" />
            <div className="mb-3 text-sm text-gold" data-id="element-25" data-mp="j080ijs09jog">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-slate-700" data-id="element-26" data-mp="ezegi1hoop0u">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-pink-100 pt-4" data-id="element-27" data-mp="9za00bgrzuam">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-28" data-mp="529qfqkpw9er">
                {item.initials}
              </span>
              <div data-id="element-29" data-mp="eabjp9qxusb">
                <p className="text-sm font-extrabold text-ink" data-id="element-30" data-mp="amissc4lcen">{item.name}</p>
                <p className="text-xs text-slate-500" data-id="element-31" data-mp="3hmduv0cn89gl">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-pink-100 bg-white px-4 py-12 md:py-14" data-id="element-32" data-mp="e4vxxnyesmuy">
      <div className="mx-auto max-w-4xl" data-id="element-33" data-mp="j1vth4xmu77t">
        <Eyebrow danger data-id="element-34" data-mp="j51hshyft52h">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-35" data-mp="4d9gvaze3tka">
          ¿Cuántos domingos más vas a <span className="text-primary" data-id="element-36" data-mp="4cgcuhrtfi5u">perder armando clases?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-37" data-mp="9b14hst04tio">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-pink-100 bg-canvas p-4" data-id="element-38" data-mp="x0vhqbh8gk3g">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-39" data-mp="2mj23pzzovvj4" />
              <span className="text-sm font-semibold text-slate-700 md:text-base" data-id="element-40" data-mp="2hlabt5z1o9ex">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-41" data-mp="2w8lojb6ypzgm">
          No es falta de vocación.{' '}
          <span className="font-black text-primary" data-id="element-42" data-mp="2rcwx5v53jove">Es falta de un sistema.</span>
        </p>
        <div className="mt-7 text-center" data-id="element-43" data-mp="23lhozlqn2dc8">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-44" data-mp="1ymemo921ml7y">
            SÍ, QUIERO RECUPERAR MIS DOMINGOS →
          </Button>
        </div>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-45" data-mp="3o12wq3n5vr2t">
      <Eyebrow data-id="element-46" data-mp="3nzi44jkeqi7l">
        <span className="inline-flex items-center gap-2" data-id="element-47" data-mp="3o35vbc2dqsj5">
          <TrophyIcon className="h-4 w-4" data-id="element-48" data-mp="3jyg1rtv71q1e" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-49" data-mp="j5bkke1aomvx">
        5 módulos. <span className="text-primary" data-id="element-50" data-mp="j632im1yym3q">Principiante → Avanzado.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-slate-600" data-id="element-51" data-mp="n6dhupbniov9">
        450+ páginas con clases, combinaciones y adaptaciones listas para dictar.
      </p>

      <div className="mt-8 space-y-3" data-id="element-52" data-mp="x4b81id2mvqi">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary shadow-lg shadow-pink-100' : 'border-pink-100'}`} data-id="element-53" data-mp="123e1e5jovdin" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-54" data-mp="1avzjdjfhxg6f">
              <span className="flex items-center gap-3" data-id="element-55" data-mp="1psgtk7by8ore">
                <span className="text-2xl" data-id="element-56" data-mp="1pt8gktrhde62">{mod.icon}</span>
                <span data-id="element-57" data-mp="3ab13hpqbcpzu">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-58" data-mp="3abiv29216g1f">
                    {mod.name}
                  </b>
                  <small className="font-bold text-primary" data-id="element-59" data-mp="30clb7jfjvaor">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-60" data-mp="2ve09ffkngiy7" />
            </button>
            <AnimatePresence initial={false} data-id="element-61" data-mp="3o1w30bex9uqr">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-62" data-mp="3jb60d5xvs5zr">
                  <ul className="space-y-2 border-t border-pink-100 px-5 py-4 text-sm font-semibold text-slate-600 md:px-6" data-id="element-63" data-mp="fnnlbydxuodd">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-64" data-mp="254srz68cir5i">
                        <span className="text-primary" data-id="element-65" data-mp="2a32p3mdms1tr">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-pink-100 bg-white px-4 py-12 md:py-14" data-id="element-66" data-mp="1mc6y2rh01ank">
      <div className="mx-auto max-w-6xl" data-id="element-67" data-mp="1qfmvvr4t1h1u">
        <Eyebrow data-id="element-68" data-mp="1qch7ke402j76">
          <span className="inline-flex items-center gap-2" data-id="element-69" data-mp="1cesn37duhe2n">
            <GiftIcon className="h-4 w-4" data-id="element-70" data-mp="1cgdh8vw8yuzz" /> 3 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-71" data-mp="1he5jojuipzif">
          Además te llevás <span className="text-primary" data-id="element-72" data-mp="36t3arcipe26u">3 bonos gratis</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-73" data-mp="36r0jkp5te30j">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-pink-100 bg-canvas p-5 shadow-sm" data-id="element-74" data-mp="103ruxpsfcdy0">
              <div className="flex gap-3" data-id="element-75" data-mp="v3nbbqw7z33k">
                <span className="text-3xl" data-id="element-76" data-mp="19uml358sjxoq">{icon}</span>
                <div data-id="element-77" data-mp="19w7eusna7clj">
                  <small className="font-extrabold uppercase tracking-wide text-primary" data-id="element-78" data-mp="153xnr9afk6ho">
                    Bono {number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-79" data-mp="1it7wmgfuicg0">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-80" data-mp="1ssysalg56xho">
                {description}
              </p>
              <div className="mt-auto flex items-center border-t border-pink-100 pt-4 text-sm font-extrabold" data-id="element-81" data-mp="1nvgrjica3okr">
                <span className="text-slate-400 line-through" data-id="element-82" data-mp="3ul53kad5psvc">{value}</span>
                <span className="ml-auto rounded-full bg-primary/10 px-3 py-1 text-primary" data-id="element-83" data-mp="3pmk3caxwn96w">
                  GRATIS
                </span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-slate-600 md:text-lg" data-id="element-84" data-mp="38q4woh3ou0pp">
          Valor total de bonos: <s className="text-slate-400" data-id="element-85" data-mp="3dioa9e2dkvui">$38.000</s> →{' '}
          <span className="font-black text-primary" data-id="element-86" data-mp="3ihr38052zbm4">incluidos gratis hoy</span>
        </p>
      </div>
    </section>;
}
export function ResultsAndGuarantee() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-87" data-mp="2kvtswq0uucl9">
      <Eyebrow data-id="element-88" data-mp="2pt3pcpmnvoil">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-89" data-mp="2urof1v4aepss">
        Instructoras que ya <span className="text-primary" data-id="element-90" data-mp="2uqls1ya357i5">dieron vuelta su año</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-91" data-mp="2ytianozml3m3">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-pink-100 bg-white p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-92" data-mp="a1z1rmj8fc27">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-93" data-mp="e4vq7tvge2e2">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-94" data-mp="1ymwf3movez49">
                {item.initials}
              </span>
              <div data-id="element-95" data-mp="1ktx49y019t31">
                <p className="text-sm font-extrabold text-ink" data-id="element-96" data-mp="1kslxrqf7e788">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-slate-500" data-id="element-97" data-mp="1fubz35u9sohv">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-pink-100 pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-98" data-mp="1aupqxatatyfx">
              <div className="flex flex-wrap items-center gap-3" data-id="element-99" data-mp="16w02mn6q2whi">
                <span className="text-sm text-gold" data-id="element-100" data-mp="11w49jizkcpgq">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary" data-id="element-101" data-mp="317n60y3ok59i">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-slate-700" data-id="element-102" data-mp="254mu6s2hppuw">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>

      <div className="mt-10 flex flex-col items-center gap-5 rounded-3xl border-2 border-primary/20 bg-white p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-103" data-mp="2e6ck1tgq4gqk">
        <span className="inline-flex shrink-0 rounded-full border-2 border-primary bg-canvas p-4" data-id="element-104" data-mp="1lh5dq8b12mur">
          <ShieldIcon className="h-9 w-9 text-primary" data-id="element-105" data-mp="1hfk5x1dcoyrf" />
        </span>
        <div data-id="element-106" data-mp="1hhesek49syxm">
          <h3 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-107" data-mp="1hdh8vxds65d7">
            Garantía de 7 días, sin preguntas
          </h3>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-108" data-mp="1qfqeq9y3tak9">
            Abrís los módulos y los usás en tus clases. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100%. Sin formularios, sin drama. Tasa de
            devoluciones: <b className="text-primary" data-id="element-109" data-mp="39eel36ytk14f">menos del 0,5%</b>.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-pink-100 bg-white px-4 py-12 md:py-14" data-id="element-110" data-mp="30ehz5d3tdgd2">
      <div className="mx-auto max-w-3xl" data-id="element-111" data-mp="35dciqh9lv8y8">
        <Eyebrow data-id="element-112" data-mp="35ee5hizzhjvs">
          <span className="inline-flex items-center gap-2" data-id="element-113" data-mp="35dumdyylkpg6">
            <HelpCircleIcon className="h-4 w-4" data-id="element-114" data-mp="3t2dzcrsldqel" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-115" data-mp="zednzqx5r8o">
          Lo que más <span className="text-primary" data-id="element-116" data-mp="yl6wyryn1qj">nos preguntan</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-117" data-mp="3j3y3kbimdsyr">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary/50 shadow-lg shadow-pink-100' : 'border-pink-100'}`} data-id="element-118" data-mp="3o2j5qghfmmpu" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-119" data-mp="1smb33wjbkk1g">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-120" data-mp="1evo566lduy6f" />
              </button>
              <AnimatePresence initial={false} data-id="element-121" data-mp="14vx6rttpbz12">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-pink-100 px-5 py-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-122" data-mp="10ulgdm3079rt">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
        <div className="mt-9 rounded-2xl bg-canvas p-6 text-center" data-id="element-123" data-mp="32r2qtoq2wgay">
          <p className="text-base font-black text-ink md:text-xl" data-id="element-124" data-mp="37kosklupezrz">
            El próximo domingo puede ser diferente.
          </p>
          <p className="mt-1 text-sm font-semibold italic text-primary" data-id="element-125" data-mp="3ckt9gczixwiq">
            “¡Finalmente un domingo sin Jumping!” 🏃‍♀️
          </p>
          <div className="mt-5" data-id="element-126" data-mp="2jtjdi8wl8biq">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-127" data-mp="2jul0nbqvoogt">
              VER EL COMBO JUMPING 2026 →
            </Button>
          </div>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500" data-id="element-128" data-mp="2sv95bfudtch1">
            <span className="flex items-center gap-1" data-id="element-129" data-mp="425b8edkdeby">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-130" data-mp="848mp308gix8" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-131" data-mp="6n379mwkgke1">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-132" data-mp="6nlefekroj0q" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-1" data-id="element-133" data-mp="1q3018nzdblc">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-134" data-mp="glsrhmbxqgxg" /> Pago seguro
            </span>
          </p>
        </div>
      </div>
    </section>;
}